import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { downloadCreditNote } from "../../api/generateCreditNoteAPI";
import {
  dialogPaperStyle,
  dialogTitleStyle,
  dialogContentStyle,
  textFieldStyle,
  downloadButtonStyle,
  dialogActionsStyle,
} from "./DownloadCreditNoteModal.styles";

interface DownloadCreditNoteModalProps {
  open: boolean;
  onClose: () => void;
}

const DownloadCreditNoteModal: React.FC<DownloadCreditNoteModalProps> = ({
  open,
  onClose,
}) => {
  const [cnId, setCnId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setCnId("");
      setError("");
    }
  }, [open]);

  const validateCnId = (id: string) => {
    if (!id.trim()) return "Please enter CNID";
    const regex = /^CN\/SER\/\d{2}-\d{5}$/;
    if (!regex.test(id.trim())) return "Format must be CN/SER/YY-XXXXX";
    return "";
  };

  const handleDownload = async () => {
    const validationError = validateCnId(cnId);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");

    try {
      const url = await downloadCreditNote(cnId.trim());

      if (url) {
        const response = await fetch(url);
        const blob = await response.blob();

        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", `credit-note-${cnId}.pdf`);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        onClose();
      }
    } catch (err) {
      console.error("Failed to download credit note", err);
      setError("Failed to download credit note. Please try again.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: dialogPaperStyle }}>
      <DialogTitle sx={dialogTitleStyle}>
        CN ID*
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={dialogContentStyle}>
        <TextField
          sx={textFieldStyle}
          fullWidth
          placeholder="Enter Credit Note ID"
          value={cnId}
          onChange={(e) => setCnId(e.target.value)}
          error={!!error}
          helperText={error}
        />
      </DialogContent>

      <DialogActions sx={dialogActionsStyle}>
        <Button
          variant="contained"
          onClick={handleDownload}
          sx={downloadButtonStyle}
        >
          Download Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadCreditNoteModal;
